import { useState } from "react";
import { useLanguage } from "../../context/Language/LanguageContext";
import { kVolleyballScoringPrimer } from "../utils/aiProviders";
import { getRotationLabels, getRotationNaming } from "../utils/Constants";
import { commonSideForMatch, findCommonTeamName } from "../utils/MatchCSV";
import {
    addStatsItem,
    calculateAllStats,
    createStatsItem,
} from "../utils/StatsItem";
import AIReportPanel from "./AIReportPanel";
import DeepAnalysisPanel from "./DeepAnalysisPanel";

function pct(v) {
  if (v === undefined || v === null || isNaN(v)) {
    return "n/a";
  }
  return `${v.toFixed(0)}%`;
}

function num(v, digits = 2) {
  if (v === undefined || v === null || isNaN(v)) {
    return "n/a";
  }
  return v.toFixed(digits);
}

// The two AI report surfaces (AI State of Play + Deep AI Analysis), extracted
// from Summary so they can live on the dedicated AI Reports page. Consumes the
// processed match objects plus the toolbar's set/team selection.
function AIReportsTabs({ matches, selectedGame, selectedTeam }) {
  const [displayScreen, setDisplayScreen] = useState(0);
  const { t } = useLanguage();

  // Aggregate team-level stats. Team A is the tracked team and Team B the
  // opponent(s). When several matches are loaded the tracked team is the one
  // present in every match (it may be the home side in some and the away side
  // in others), so we resolve which side is the common team per match; for a
  // single match Team A = first/home team (m.teamA), Team B = second/away team.
  const buildTeamTotals = () => {
    if (!matches || matches.length === 0) {
      return null;
    }
    const teamAAgg = createStatsItem(null, undefined);
    const teamBAgg = createStatsItem(null, undefined);

    const teamTotal = (items) =>
      items ? items.find((si) => si.player == null) : null;

    const commonName = findCommonTeamName(matches);

    for (var m of matches) {
      // Which side of THIS match is the tracked (Team A) team. Defaults to the
      // home side when there is no single common team (e.g. a single match).
      const commonSide = commonSideForMatch(m, commonName) || "A";
      let homeItem = null;
      let awayItem = null;
      if (!selectedGame || selectedGame === 0) {
        homeItem = teamTotal(m.teamA.statsItems);
        awayItem = teamTotal(m.teamB.statsItems);
      } else {
        const s = m.sets ? m.sets[selectedGame - 1] : null;
        if (s) {
          homeItem = teamTotal(s.teamAStatsItems);
          awayItem = teamTotal(s.teamBStatsItems);
        }
      }
      const aItem = commonSide === "B" ? awayItem : homeItem;
      const bItem = commonSide === "B" ? homeItem : awayItem;
      if (aItem) {
        addStatsItem(aItem, teamAAgg);
      }
      if (bItem) {
        addStatsItem(bItem, teamBAgg);
      }
    }

    calculateAllStats(teamAAgg);
    calculateAllStats(teamBAgg);

    const commonSide0 = commonSideForMatch(matches[0], commonName) || "A";
    return {
      teamA: {
        name:
          commonSide0 === "B" ? matches[0].teamB.Name : matches[0].teamA.Name,
        stats: teamAAgg,
      },
      teamB: {
        name:
          commonSide0 === "B" ? matches[0].teamA.Name : matches[0].teamB.Name,
        stats: teamBAgg,
      },
    };
  };

  const describeTeam = (label, si) => {
    const lines = [];
    lines.push(`${label}:`);
    lines.push(
      `- Sideout %: ${pct(si.SideOutPercent ? si.SideOutPercent[0] : NaN)}`
    );
    lines.push(`- Breakpoint / point-on-serve %: ${pct(si.pointPercent)}`);
    lines.push(
      `- Serving: ${num(si.ServeTotal, 0)} serves, ${num(
        si.Serve3,
        0
      )} aces, ${num(si.Serve0, 0)} errors, good-serve ${pct(
        si.GoodServePercent
      )}, efficiency ${num(si.ServeEfficiency * 100, 0)}%`
    );
    lines.push(
      `- Attack: ${num(si.SpikeTotal, 0)} attempts, ${num(
        si.Spike3,
        0
      )} kills, ${num(si.Spike0, 0)} errors, ${num(
        si.SpikeBlocked,
        0
      )} blocked, efficiency ${num(si.SpikeEfficiency * 100, 0)}%, kill% ${num(
        si.SpikeKillPercentage * 100,
        0
      )}%`
    );
    lines.push(`- Block points: ${num(si.BlockPoints, 0)}`);
    lines.push(
      `- Reception: ${num(si.PassTotal, 0)} receptions, avg ${num(
        si.PassAverage
      )}, ${num(si.PassPerfect, 0)} perfect, ${num(si.PassError, 0)} errors`
    );
    lines.push(`- Digs: ${num(si.Dig, 0)}, dig avg ${num(si.DigAverage)}`);
    lines.push(
      `- Total unforced errors (serve+pass+attack+block+dig): ${num(
        si.minus,
        0
      )}, ball-handling errors: ${num(si.BHE, 0)}`
    );

    // Per-rotation sideout to surface rotation strengths/weaknesses.
    if (si.SideOutPercent) {
      const rotationLabels = getRotationLabels();
      const naming = getRotationNaming();
      const rot = [];
      for (var i = 1; i <= 6; i++) {
        rot.push(`${rotationLabels[i - 1]} ${pct(si.SideOutPercent[i])}`);
      }
      const legend =
        naming === "R"
          ? "R# = rotation sequence, R1 = setter in position 1"
          : "S# = setter court position";
      lines.push(`- Sideout % by rotation (${legend}): ${rot.join(", ")}`);
    }
    return lines.join("\n");
  };

  const buildStateOfPlayPrompt = () => {
    const totals = buildTeamTotals();
    if (!totals) {
      return null;
    }
    const { teamA, teamB } = totals;
    const isMulti = matches.length > 1;
    const scope = isMulti
      ? `${matches.length} matches`
      : !selectedGame || selectedGame === 0
      ? "the full match"
      : `set ${selectedGame}`;

    // Read a set's score with the tracked team (Team A) first. Different
    // importers expose it as teamAScores/teamBScores or HomeScore/AwayScore.
    // `commonSide` is "A" or "B": which side of this match is the tracked team;
    // when it is "B" the home/away scores are swapped so Team A is always the
    // tracked team.
    const commonName = findCommonTeamName(matches);
    const setScoresOf = (s, commonSide) => {
      const rawHome = s.teamAScores != null ? s.teamAScores : s.HomeScore;
      const rawAway = s.teamBScores != null ? s.teamBScores : s.AwayScore;
      const home = rawHome == null || rawHome === "" ? null : Number(rawHome);
      const away = rawAway == null || rawAway === "" ? null : Number(rawAway);
      const h = home != null && !isNaN(home) ? home : null;
      const w = away != null && !isNaN(away) ? away : null;
      if (commonSide === "B") {
        return { a: w, b: h };
      }
      return { a: h, b: w };
    };

    // Build the authoritative match result from actual set scores so the AI is
    // GIVEN the result (decided by sets won) and never has to infer it from
    // cumulative point totals.
    const buildMatchResultBlock = () => {
      if (matches.length === 1) {
        const m = matches[0];
        if (!m.sets || m.sets.length === 0) {
          return "";
        }
        const commonSide = commonSideForMatch(m, commonName) || "A";
        let sets = m.sets;
        if (selectedGame && selectedGame !== 0) {
          const s = m.sets[selectedGame - 1];
          sets = s ? [s] : [];
        }
        const lines = ["Set-by-set scores (Team A score first):"];
        let aSets = 0;
        let bSets = 0;
        sets.forEach((s, i) => {
          const { a, b } = setScoresOf(s, commonSide);
          const idx =
            selectedGame && selectedGame !== 0 ? selectedGame : i + 1;
          let note = "";
          if (typeof a === "number" && typeof b === "number") {
            if (a > b) {
              note = " (Team A won this set)";
              aSets += 1;
            } else if (b > a) {
              note = " (Team B won this set)";
              bSets += 1;
            }
          }
          lines.push(`- Set ${idx}: Team A ${a} - ${b} Team B${note}`);
        });
        if (!selectedGame || selectedGame === 0) {
          let result;
          if (aSets > bSets) {
            result = `Team A WON the match ${aSets}-${bSets} in sets. Team B lost.`;
          } else if (bSets > aSets) {
            result = `Team B WON the match ${bSets}-${aSets} in sets. Team A lost.`;
          } else {
            result = `The sets are level ${aSets}-${bSets}.`;
          }
          lines.push("");
          lines.push(
            `MATCH RESULT (this is the ONLY correct result — it is decided by sets won, not by total points): ${result}`
          );
          lines.push(
            "Use this result as given. Do NOT re-derive, question, or contradict it using cumulative point totals."
          );
        }
        return lines.join("\n");
      }

      // Multi-match: report each match's result for Team A (the tracked team
      // that is common to every match), regardless of whether it played at home
      // or away in that match.
      const lines = [
        "Per-match results (Team A = the tracked team common to all matches, result decided by sets won):",
      ];
      let won = 0;
      let lost = 0;
      matches.forEach((m, mi) => {
        if (!m.sets) {
          return;
        }
        const commonSide = commonSideForMatch(m, commonName) || "A";
        let aSets = 0;
        let bSets = 0;
        m.sets.forEach((s) => {
          const { a, b } = setScoresOf(s, commonSide);
          if (typeof a === "number" && typeof b === "number") {
            if (a > b) {
              aSets += 1;
            } else if (b > a) {
              bSets += 1;
            }
          }
        });
        let res;
        if (aSets > bSets) {
          res = "won";
          won += 1;
        } else if (aSets < bSets) {
          res = "lost";
          lost += 1;
        } else {
          res = "drew";
        }
        lines.push(`- Match ${mi + 1}: Team A ${res} ${aSets}-${bSets} in sets`);
      });
      lines.push("");
      lines.push(
        `Across these matches Team A won ${won} and lost ${lost} (counted by sets won, never by total points).`
      );
      return lines.join("\n");
    };

    const resultBlock = buildMatchResultBlock();

    // Tell the AI exactly how to name rotations so it matches the user's chosen
    // convention (e.g. "Rotation S1" / "Rotation R3") rather than a bare number.
    const rotationLabels = getRotationLabels();
    const rotationInstruction =
      `When referring to a rotation, always use the exact rotation labels from the data ` +
      `(${rotationLabels.join(", ")}), e.g. "Rotation ${rotationLabels[0]}". ` +
      `Never refer to a rotation by a bare number such as "Rotation 1".`;

    // Across multiple matches the opponents differ, so Team B totals are not a
    // meaningful single opponent. In that case only the first team (Team A) is
    // analysed.
    if (isMulti) {
      return [
        `You are analysing the state of play of a single volleyball team across ${scope} against various opponents.`,
        "The team is referred to only as Team A.",
        "",
        "IMPORTANT — the match results below are decided by SETS WON, not by total points. Never use cumulative point totals to judge who won.",
        ...(resultBlock ? ["", resultBlock] : []),
        "",
        "Here are Team A's aggregated team statistics (for analysing quality of play only — these point/efficiency totals are NOT a scoreboard):",
        "",
        describeTeam("Team A", teamA.stats),
        "",
        rotationInstruction,
        "",
        "Write a coaching analysis of the state of play for Team A. Use Markdown with `##` section",
        "headings, `**bold**` for key figures, and bullet lists. Reference concrete numbers",
        "from the data. Do not invent statistics that are not provided; if data for a point is",
        "not available, say so briefly or omit it. Cover these sections in this order:",
        "",
        "## Overall form",
        "How well Team A is performing across these matches — sideout, breakpoint rate, serving",
        "pressure, attack efficiency, block impact, and error profile.",
        "",
        "## What Team A is doing right",
        "e.g. winning sideout with better first-ball efficiency, creating pressure from serve",
        "without leaking errors, outscoring in transition, controlling middle/right-side attack,",
        "blocking specific rotations.",
        "",
        "## What Team A needs to improve",
        "e.g. too many attack errors from certain zones, poor conversion on out-of-system balls,",
        "low block-touch rate, predictable setter distribution, sideout weakness in specific",
        "rotations.",
      ].join("\n");
    }

    // Team identities are intentionally anonymised ("Team A" / "Team B") so no
    // real team names are sent to the AI provider.
    return [
      `You are analysing the state of play between two volleyball teams across ${scope}.`,
      "The two teams are referred to only as Team A and Team B.",
      "",
      "IMPORTANT — the match result below is decided by SETS WON, not by total points. A team can score more total points and still lose. Never use cumulative point totals to judge who is ahead or who won.",
      ...(resultBlock ? ["", resultBlock] : []),
      "",
      "Here are the aggregated team statistics (for analysing quality of play only — these point/efficiency totals are NOT a scoreboard):",
      "",
      describeTeam("Team A", teamA.stats),
      "",
      describeTeam("Team B", teamB.stats),
      "",
      rotationInstruction,
      "",
      "Write a coaching analysis of the state of play. Use Markdown with `##` section",
      "headings, `**bold**` for key figures, and bullet lists. Reference concrete numbers",
      "from the data. Do not invent statistics that are not provided; if data for a point is",
      "not available, say so briefly or omit it. Cover these sections in this order:",
      "",
      "## Match control",
      "Which team is doing the important things better and why — compare sideout, breakpoint",
      "rate, serving pressure, attack efficiency, block impact, and error profile.",
      "",
      "## What Team A is doing right",
      "e.g. winning sideout with better first-ball efficiency, creating pressure from serve",
      "without leaking errors, outscoring in transition, controlling middle/right-side attack,",
      "blocking specific rotations.",
      "",
      "## What Team A needs to improve",
      "e.g. too many attack errors from certain zones, poor conversion on out-of-system balls,",
      "weak serving against a specific passer, low block-touch rate, predictable setter",
      "distribution.",
      "",
      "## What Team B is doing right",
      "areas where they are competitive or ahead, even if behind on the scoreboard.",
      "",
      "## What Team B needs to improve",
      "e.g. reception quality, transition conversion, opponent hitting efficiency allowed,",
      "missed serves at bad times, setter/hitter connection by rotation.",
    ].join("\n");
  };

  const canGenerate = matches && matches.length > 0;
  const selectedTeamLabel = selectedTeam === 0 ? "Team A" : "Team B";
  const tabClass = (idx) => `tab ${displayScreen === idx ? "tab-active" : ""}`;

  return (
    <div className="flex-col">
      <div className="tabs tabs-boxed inline-flex mb-2">
        <button className={tabClass(0)} onClick={() => setDisplayScreen(0)}>
          {t("AI Report")}
        </button>
        <button className={tabClass(1)} onClick={() => setDisplayScreen(1)}>
          {t("Deep AI Analysis")}
        </button>
      </div>
      <div>
        {displayScreen === 1 ? (
          <DeepAnalysisPanel
            matches={matches}
            selectedGame={selectedGame}
            selectedTeam={selectedTeam}
            selectedTeamLabel={selectedTeamLabel}
            canGenerate={canGenerate}
          />
        ) : (
          <AIReportPanel
            title="AI State of Play Report"
            documentTitle="State of Play Report"
            generateLabel="Generate general report"
            systemPrompt={
              "You are an expert volleyball performance analyst and head coach. You produce clear, practical, evidence-based match analysis. " +
              kVolleyballScoringPrimer
            }
            buildPrompt={buildStateOfPlayPrompt}
            canGenerate={canGenerate}
            notReadyMessage="Load at least one match to generate a report."
            followUpStorageKey="VBLiveFollowUpQuestions_stateofplay"
          />
        )}
      </div>
    </div>
  );
}

export default AIReportsTabs;
